import { useState, useEffect } from 'react';
import {
  TrendingUp,
  BarChart3,
  Target,
  Activity,
  Search,
  RefreshCw,
  Eye,
  Trash2,
  Sparkles,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  forecastingApi,
  DemandForecast,
  ForecastDashboard,
  ForecastMethod,
  ForecastStatus,
  GenerateForecastDto,
  HistoricalDemand,
} from '@/api/services/forecasting.api';
import { inventoryApi, InventoryItem } from '@/api/services/inventory.api';
import { locationsApi, Location } from '@/api/services/locations.api';

function getStatusBadgeVariant(status: ForecastStatus): 'default' | 'secondary' | 'outline' {
  switch (status) {
    case 'ACTIVE':
      return 'default';
    case 'DRAFT':
      return 'secondary';
    case 'ARCHIVED':
      return 'outline';
    default:
      return 'secondary';
  }
}

function getMethodLabel(method: ForecastMethod): string {
  switch (method) {
    case 'MOVING_AVERAGE':
      return 'Moving Average';
    case 'EXPONENTIAL_SMOOTHING':
      return 'Exp. Smoothing';
    case 'LINEAR_REGRESSION':
      return 'Linear Regression';
    case 'SEASONAL':
      return 'Seasonal';
    case 'MACHINE_LEARNING':
      return 'Machine Learning';
    default:
      return method;
  }
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function Forecasting() {
  const [dashboard, setDashboard] = useState<ForecastDashboard | null>(null);
  const [forecasts, setForecasts] = useState<DemandForecast[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [methodFilter, setMethodFilter] = useState<string>('all');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Modal states
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedForecast, setSelectedForecast] = useState<DemandForecast | null>(null);
  const [historicalData, setHistoricalData] = useState<HistoricalDemand[]>([]);

  // Form state
  const [generateForm, setGenerateForm] = useState<GenerateForecastDto>({
    itemId: '',
    forecastMethod: 'MOVING_AVERAGE',
    forecastPeriod: 'MONTHLY',
    periodsToForecast: 12,
    lookbackPeriods: 12,
    smoothingFactor: 0.3,
  });

  // Items and locations for selects
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [_loadingItems, setLoadingItems] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchForecasts();
    fetchItems();
    fetchLocations();
  }, []);

  useEffect(() => {
    fetchForecasts();
  }, [searchQuery, statusFilter, methodFilter, pagination.page]);

  const fetchDashboard = async () => {
    try {
      const response = await forecastingApi.getDashboard();
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  };

  const fetchForecasts = async () => {
    try {
      setLoading(true);
      const response = await forecastingApi.getAll({
        search: searchQuery || undefined,
        status: statusFilter !== 'all' ? (statusFilter as ForecastStatus) : undefined,
        forecastMethod: methodFilter !== 'all' ? (methodFilter as ForecastMethod) : undefined,
        page: pagination.page,
        limit: pagination.limit,
      });
      setForecasts(response.data.data);
      setPagination((prev) => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages,
      }));
    } catch (error) {
      console.error('Failed to fetch forecasts:', error);
      toast.error('Failed to load forecasts');
    } finally {
      setLoading(false);
    }
  };

  const fetchItems = async () => {
    try {
      setLoadingItems(true);
      const response = await inventoryApi.getAll({ limit: 500 });
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch items:', error);
    } finally {
      setLoadingItems(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationsApi.getAll({ limit: 100 });
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const handleGenerateForecast = async () => {
    if (!generateForm.itemId) {
      toast.error('Please select an inventory item');
      return;
    }

    try {
      const response = await forecastingApi.generate(generateForm);
      toast.success('Forecast generated successfully');
      setShowGenerateModal(false);
      setGenerateForm({
        itemId: '',
        forecastMethod: 'MOVING_AVERAGE',
        forecastPeriod: 'MONTHLY',
        periodsToForecast: 12,
        lookbackPeriods: 12,
        smoothingFactor: 0.3,
      });
      fetchForecasts();
      fetchDashboard();
      // Show the generated forecast
      setSelectedForecast(response.data);
      setShowDetailModal(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to generate forecast');
    }
  };

  const handleViewForecast = async (forecast: DemandForecast) => {
    try {
      const response = await forecastingApi.getById(forecast.id);
      setSelectedForecast(response.data);
      // Fetch historical data for this item
      const historicalResponse = await forecastingApi.getHistoricalDemand(forecast.itemId, {
        locationId: forecast.locationId || undefined,
        limit: 24,
      });
      setHistoricalData(historicalResponse.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load forecast details');
    }
  };

  const handleActivateForecast = async (forecast: DemandForecast) => {
    try {
      await forecastingApi.update(forecast.id, { status: 'ACTIVE' });
      toast.success('Forecast activated');
      fetchForecasts();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to activate forecast');
    }
  };

  const handleArchiveForecast = async (forecast: DemandForecast) => {
    try {
      await forecastingApi.update(forecast.id, { status: 'ARCHIVED' });
      toast.success('Forecast archived');
      fetchForecasts();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to archive forecast');
    }
  };

  const handleDeleteForecast = async (forecast: DemandForecast) => {
    if (!confirm('Are you sure you want to delete this forecast?')) return;

    try {
      await forecastingApi.delete(forecast.id);
      toast.success('Forecast deleted');
      fetchForecasts();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to delete forecast');
    }
  };

  const handleSyncHistorical = async () => {
    if (!generateForm.itemId) {
      toast.error('Please select an item first');
      return;
    }

    try {
      const response = await forecastingApi.syncHistorical({
        itemId: generateForm.itemId,
        locationId: generateForm.locationId,
      });
      toast.success(`Synced ${response.data.recordsSynced} historical records`);
    } catch (error) {
      toast.error('Failed to sync historical data');
    }
  };

  const handleCalculateAccuracy = async () => {
    try {
      const response = await forecastingApi.calculateAccuracy({ monthsBack: 6 });
      toast.success(`Calculated accuracy for ${response.data.periodsCalculated} periods`);
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to calculate accuracy');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
            Demand Forecasting
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Generate and manage demand forecasts for inventory planning
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleCalculateAccuracy}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Calculate Accuracy
          </Button>
          <Button onClick={() => setShowGenerateModal(true)}>
            <Sparkles className="h-4 w-4 mr-2" />
            Generate Forecast
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Total Forecasts
                </p>
                <p className="text-3xl font-semibold text-slate-900 dark:text-white">
                  {loading ? <Skeleton className="h-9 w-16" /> : dashboard?.kpis.totalForecasts || 0}
                </p>
              </div>
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <BarChart3 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Active Forecasts
                </p>
                <p className="text-3xl font-semibold text-slate-900 dark:text-white">
                  {loading ? <Skeleton className="h-9 w-16" /> : dashboard?.kpis.activeForecasts || 0}
                </p>
              </div>
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <Activity className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Avg Accuracy (MAPE)
                </p>
                <p className="text-3xl font-semibold text-slate-900 dark:text-white">
                  {loading ? (
                    <Skeleton className="h-9 w-16" />
                  ) : dashboard?.kpis.averageMape !== null ? (
                    `${dashboard?.kpis.averageMape}%`
                  ) : (
                    'N/A'
                  )}
                </p>
              </div>
              <div className="p-2 bg-violet-100 dark:bg-violet-900/30 rounded-lg">
                <Target className="h-5 w-5 text-violet-600 dark:text-violet-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Accuracy Trend
                </p>
                <p className="text-3xl font-semibold text-slate-900 dark:text-white">
                  {loading ? (
                    <Skeleton className="h-9 w-16" />
                  ) : dashboard?.kpis.accuracyTrend !== null ? (
                    <span className="flex items-center gap-1">
                      {dashboard!.kpis.accuracyTrend > 0 ? (
                        <>
                          <ArrowUpRight className="h-5 w-5 text-emerald-500" />
                          <span className="text-emerald-600">+{dashboard?.kpis.accuracyTrend}%</span>
                        </>
                      ) : (
                        <>
                          <ArrowDownRight className="h-5 w-5 text-red-500" />
                          <span className="text-red-600">{dashboard?.kpis.accuracyTrend}%</span>
                        </>
                      )}
                    </span>
                  ) : (
                    'N/A'
                  )}
                </p>
              </div>
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <TrendingUp className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search forecasts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="DRAFT">Draft</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="ARCHIVED">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Select value={methodFilter} onValueChange={setMethodFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Method" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Methods</SelectItem>
            <SelectItem value="MOVING_AVERAGE">Moving Average</SelectItem>
            <SelectItem value="EXPONENTIAL_SMOOTHING">Exp. Smoothing</SelectItem>
            <SelectItem value="LINEAR_REGRESSION">Linear Regression</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Forecasts Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Date Range</TableHead>
                <TableHead>Confidence</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  </TableRow>
                ))
              ) : forecasts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    No forecasts found. Generate your first forecast to get started.
                  </TableCell>
                </TableRow>
              ) : (
                forecasts.map((forecast) => (
                  <TableRow key={forecast.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{forecast.item.name}</p>
                        <p className="text-xs text-slate-500">{forecast.item.sku}</p>
                      </div>
                    </TableCell>
                    <TableCell>{forecast.location?.name || 'All Locations'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{getMethodLabel(forecast.forecastMethod)}</Badge>
                    </TableCell>
                    <TableCell>{forecast.forecastPeriod}</TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {formatDate(forecast.startDate)} - {formatDate(forecast.endDate)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {forecast.confidence !== null ? (
                        <span className={forecast.confidence >= 70 ? 'text-emerald-600' : forecast.confidence >= 50 ? 'text-amber-600' : 'text-red-600'}>
                          {forecast.confidence}%
                        </span>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(forecast.status)}>
                        {forecast.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewForecast(forecast)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {forecast.status === 'DRAFT' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleActivateForecast(forecast)}
                          >
                            <Activity className="h-4 w-4 text-emerald-500" />
                          </Button>
                        )}
                        {forecast.status === 'ACTIVE' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleArchiveForecast(forecast)}
                          >
                            <Calendar className="h-4 w-4 text-slate-500" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteForecast(forecast)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page === 1}
              onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page === pagination.totalPages}
              onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Generate Forecast Modal */}
      <Dialog open={showGenerateModal} onOpenChange={setShowGenerateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Generate Demand Forecast</DialogTitle>
            <DialogDescription>
              Configure the forecast parameters and generate predictions based on historical consumption data.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Inventory Item *</Label>
              <Select
                value={generateForm.itemId}
                onValueChange={(value) => setGenerateForm({ ...generateForm, itemId: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an item" />
                </SelectTrigger>
                <SelectContent>
                  {items.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name} ({item.sku})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Location (Optional)</Label>
              <Select
                value={generateForm.locationId || 'all'}
                onValueChange={(value) =>
                  setGenerateForm({ ...generateForm, locationId: value === 'all' ? undefined : value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name} ({loc.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Forecast Method</Label>
                <Select
                  value={generateForm.forecastMethod}
                  onValueChange={(value) =>
                    setGenerateForm({ ...generateForm, forecastMethod: value as ForecastMethod })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MOVING_AVERAGE">Moving Average</SelectItem>
                    <SelectItem value="EXPONENTIAL_SMOOTHING">Exp. Smoothing</SelectItem>
                    <SelectItem value="LINEAR_REGRESSION">Linear Regression</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Forecast Period</Label>
                <Select
                  value={generateForm.forecastPeriod}
                  onValueChange={(value) => setGenerateForm({ ...generateForm, forecastPeriod: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DAILY">Daily</SelectItem>
                    <SelectItem value="WEEKLY">Weekly</SelectItem>
                    <SelectItem value="MONTHLY">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Periods to Forecast</Label>
                <Input
                  type="number"
                  min={1}
                  max={52}
                  value={generateForm.periodsToForecast}
                  onChange={(e) =>
                    setGenerateForm({ ...generateForm, periodsToForecast: parseInt(e.target.value) || 12 })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Lookback Periods</Label>
                <Input
                  type="number"
                  min={3}
                  max={52}
                  value={generateForm.lookbackPeriods}
                  onChange={(e) =>
                    setGenerateForm({ ...generateForm, lookbackPeriods: parseInt(e.target.value) || 12 })
                  }
                />
              </div>
            </div>

            {generateForm.forecastMethod === 'EXPONENTIAL_SMOOTHING' && (
              <div className="space-y-2">
                <Label>Smoothing Factor (α)</Label>
                <Input
                  type="number"
                  min={0.01}
                  max={1}
                  step={0.05}
                  value={generateForm.smoothingFactor}
                  onChange={(e) =>
                    setGenerateForm({ ...generateForm, smoothingFactor: parseFloat(e.target.value) || 0.3 })
                  }
                />
                <p className="text-xs text-slate-500">
                  Higher values give more weight to recent observations (0.1-0.3 recommended)
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Add any notes about this forecast..."
                value={generateForm.notes || ''}
                onChange={(e) => setGenerateForm({ ...generateForm, notes: e.target.value })}
              />
            </div>

            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleSyncHistorical} disabled={!generateForm.itemId}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Sync Historical Data
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerateForecast}>
              <Sparkles className="h-4 w-4 mr-2" />
              Generate Forecast
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Forecast Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Forecast Details</DialogTitle>
            <DialogDescription>
              {selectedForecast?.item.name} ({selectedForecast?.item.sku})
            </DialogDescription>
          </DialogHeader>

          {selectedForecast && (
            <Tabs defaultValue="periods" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="periods">Forecast Periods</TabsTrigger>
                <TabsTrigger value="historical">Historical Data</TabsTrigger>
              </TabsList>

              <TabsContent value="periods" className="space-y-4">
                <div className="grid grid-cols-3 gap-4 py-4">
                  <div>
                    <p className="text-sm text-slate-500">Method</p>
                    <p className="font-medium">{getMethodLabel(selectedForecast.forecastMethod)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Confidence</p>
                    <p className="font-medium">
                      {selectedForecast.confidence !== null ? `${selectedForecast.confidence}%` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">MAPE</p>
                    <p className="font-medium">
                      {selectedForecast.mape !== null ? `${selectedForecast.mape.toFixed(2)}%` : 'N/A'}
                    </p>
                  </div>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead className="text-right">Predicted</TableHead>
                      <TableHead className="text-right">Actual</TableHead>
                      <TableHead className="text-right">Lower Bound</TableHead>
                      <TableHead className="text-right">Upper Bound</TableHead>
                      <TableHead className="text-right">Variance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedForecast.periods.map((period) => (
                      <TableRow key={period.id}>
                        <TableCell>
                          {formatDate(period.periodStart)} - {formatDate(period.periodEnd)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {period.predictedDemand.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right">
                          {period.actualDemand !== null ? period.actualDemand.toFixed(2) : '-'}
                        </TableCell>
                        <TableCell className="text-right text-slate-500">
                          {period.lowerBound !== null ? period.lowerBound.toFixed(2) : '-'}
                        </TableCell>
                        <TableCell className="text-right text-slate-500">
                          {period.upperBound !== null ? period.upperBound.toFixed(2) : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {period.variance !== null ? (
                            <span className={period.variance > period.predictedDemand * 0.2 ? 'text-red-600' : 'text-emerald-600'}>
                              {period.variance.toFixed(2)}
                            </span>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TabsContent>

              <TabsContent value="historical" className="space-y-4">
                {historicalData.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No historical data available for this item.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                        <TableHead>Source</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historicalData.map((record) => (
                        <TableRow key={record.id}>
                          <TableCell>{formatDate(record.periodDate)}</TableCell>
                          <TableCell>{record.location?.name || 'N/A'}</TableCell>
                          <TableCell className="text-right font-medium">{record.quantity}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{record.source}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
